import React, { useEffect, useState } from 'react';
import {
  ArrowRightOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  PauseOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Col, Row, Space, Spin } from 'antd';
import { toCurrency, toEuroSimpleDisplayK } from 'utils/utils';
import GetLabel from 'DisplayFramework/components/GetLabel';
import IBasket from 'icons/IBasket';
import IPen from 'icons/IPen';
import IUplift from 'icons/IUplift';
import ProgressBarChart from 'components/theme_components/ProgressBarChart';
import TaskSButtonSFS from 'pages/workflow/workflowSFS/TasksButtonSFS';
import Title from 'antd/lib/typography/Title';
import {
  BonificationResponse,
  getLatestBonification,
} from 'services/parametersService';
import { TpAuthorizationResponse } from 'services/thirdParty';
import { getOfferDetailByOfferId } from 'services/pricing';

export type ActivityCardProps = {
  data: TpAuthorizationResponse;
  refetch: any;
  tptype: string;
};

const isDateExpired = (endDate: any) => {
  const today = new Date();
  const end = new Date(endDate);
  return end <= today;
};

const ActivityCard = ({ data, refetch, tptype }: ActivityCardProps) => {
  const isExpired = isDateExpired(data.enddate) ?? true;
  const [bonificationData, setBonificationData] =
    useState<BonificationResponse | null>(null);
  const [loadingBonification, setLoadingBonification] = useState(false);
  const [offerCode, setOfferCode] = useState<string>('');
  const [loadingOfferCode, setLoadingOfferCode] = useState(false);
  //const prcodes = data.authDetail.map(detail => detail.prcode);
  const firstPrcode = data.authDetail[0]?.prcode;
  const offerId = data.authDetail[0]?.offerid;
  const offerLabel = data.authDetail[0]?.offerlabel;
  console.log({ data });

  useEffect(() => {
    const fetchOfferCode = async () => {
      if (!offerId) {
        setOfferCode(offerLabel || '');
        return;
      }

      setLoadingOfferCode(true);
      try {
        const response = await getOfferDetailByOfferId({ offerid: offerId });
        const offerData = response.data;

        setOfferCode(offerData?.offercode || offerLabel || '');
      } catch (error) {
        console.error('Error fetching offer code:', error);
        setOfferCode(offerLabel || '');
      } finally {
        setLoadingOfferCode(false);
      }
    };

    fetchOfferCode();
  }, [offerId, offerLabel]);
  useEffect(() => {
    const fetchBonification = async () => {
      if (!data?.tpid || !firstPrcode) return;

      setLoadingBonification(true);
      try {
        const response = await getLatestBonification({
          thirdpartyId: data.tpid,
          activity: firstPrcode,
        });
        setBonificationData(response.data);
      } catch (error) {
        console.error('Error fetching bonification:', error);
        setBonificationData(null);
      } finally {
        setLoadingBonification(false);
      }
    };

    fetchBonification();
  }, [data?.tpid, firstPrcode]);

  const importAll = (r: any) => {
    const images: any = {};
    r.keys().map((item: any, index: any) => {
      images[item.replace('./', '')] = r(item);
    });
    return images;
  };

  const images: any = importAll(
    require.context('/src/assets/', false, /\.(png|jpg|jpe?g|svg)$/)
  );
  let icon;
  icon = images['expire.png'];

  return (
    <div style={{ position: 'relative', marginBottom: 15 }}>
      {isExpired && tptype === 'CONC' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(107, 113, 129, 0.17)',
            zIndex: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={icon || '/placeholder.svg'}
            style={{
              zIndex: 3,
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '4px 8px',
              borderRadius: '4px',
            }}
            alt="Expired"
          />
        </div>
      )}
      <div className="activity-card no-border-radius" style={{ zIndex: 1 }}>
        <Row gutter={0} className="h-100">
          {/* Main Content Area */}
          <Col
            xs={24}
            sm={24}
            md={18}
            lg={18}
            xl={18}
            style={{
              padding: '3rem',
              minHeight: '380px',
              alignContent: 'center',
            }}
          >
            {/* Header Section */}
            <Row align="middle" style={{ marginBottom: '24px' }}>
              <Col>
                <Title
                  level={3}
                  style={{
                    fontSize: '24px',
                    fontWeight: 500,
                    margin: 0,
                    lineHeight: '32px',
                  }}
                  className="color-blue"
                >
                  {data?.prcodelabel}
                </Title>
              </Col>
              {tptype === 'CONC' && (
                <Col>
                  <TaskSButtonSFS
                    clientName=""
                    entityId={data.tpid}
                    entityReference=""
                    entityName="THIRDPARTY"
                    currcode=""
                    funcode="evDeleteTPAuthorization"
                    wizardsteps
                    dataInput={data}
                    refetch={() => {
                      refetch();
                    }}
                    width={560}
                    title="evdeletetpauthorization"
                    titlewithName={data?.prcodelabel}
                  >
                    <IBasket
                      className="ml-2 pt-1"
                      style={{ cursor: 'pointer' }}
                    />
                  </TaskSButtonSFS>
                </Col>
              )}
            </Row>

            <Row gutter={[24, 16]} justify="space-between">
              {/* Left Column - Financial Data */}
              <Col xs={24} sm={24} md={13} lg={13} xl={13} className="mr-1">
                {/* First Row - Booked + Used = Total */}
                <Row
                  gutter={[8, 8]}
                  align="middle"
                  style={{ marginBottom: '16px' }}
                  wrap={false}
                >
                  <Col flex="1" style={{ minWidth: '80px' }}>
                    <div>
                      <p
                        className="gray-color font-weight-500 font-size-13"
                        style={{ marginBottom: '2px', lineHeight: '16px' }}
                      >
                        <GetLabel label="authbookedamount" />
                      </p>
                      <p
                        className="font-size-15 font-weight-500"
                        style={{ margin: 0, lineHeight: '20px' }}
                      >
                        {toCurrency(data?.authbookedamount)}
                      </p>
                    </div>
                  </Col>
                  <Col flex="none" style={{ padding: '0 8px' }}>
                    <PlusOutlined className="gray-color-1 font-size-16" />
                  </Col>
                  <Col flex="1" style={{ minWidth: '80px' }}>
                    <div>
                      <p
                        className="gray-color font-weight-500 font-size-13"
                        style={{ marginBottom: '2px', lineHeight: '16px' }}
                      >
                        <GetLabel label="authusedamount" />
                      </p>
                      <p
                        className="font-size-15 font-weight-500"
                        style={{ margin: 0, lineHeight: '20px' }}
                      >
                        {toCurrency(data?.authusedamount)}
                      </p>
                    </div>
                  </Col>
                  <Col flex="none" style={{ padding: '0 8px' }}>
                    <PauseOutlined className="gray-color-1 rotation font-size-16" />
                  </Col>
                  <Col flex="1" style={{ minWidth: '100px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <p
                        className="gray-color font-weight-500 font-size-13"
                        style={{ marginBottom: '2px', lineHeight: '16px' }}
                      >
                        <GetLabel label="totalConsumedAmount" />
                      </p>
                      <p
                        className="font-size-15 font-weight-500"
                        style={{ margin: 0, lineHeight: '20px' }}
                      >
                        {toCurrency(data?.totalConsumedAmount)}
                      </p>
                    </div>
                  </Col>
                </Row>

                {/* Second Row - Auth Amount and MPEA */}
                <Row
                  gutter={[16, 8]}
                  style={{ marginBottom: '16px' }}
                  align="top"
                >
                  <Col xs={24} sm={16} md={16}>
                    <div>
                      <p
                        className="gray-color font-weight-500 font-size-13"
                        style={{ marginBottom: '2px', lineHeight: '16px' }}
                      >
                        {data.authToleranceStatus === 'ACTIVE' ? (
                          <Space align="center" size={4}>
                            <GetLabel label="upliftauthamount" />
                            <InfoCircleOutlined />
                          </Space>
                        ) : (
                          <GetLabel label="authamount" />
                        )}
                      </p>
                      <Row align="middle" wrap={false} gutter={8}>
                        <Col>
                          <span
                            style={{
                              color:
                                data.authToleranceStatus === 'ACTIVE'
                                  ? '#DD7248'
                                  : 'inherit',
                              fontSize: '18px',
                              fontWeight: 500,
                              lineHeight: '24px',
                            }}
                          >
                            {toCurrency(
                              data?.authamountwithuplift
                                ? data?.authamountwithuplift
                                : data?.authamount
                            )}
                          </span>
                        </Col>
                        {tptype === 'CONC' && (
                          <Col>
                            <TaskSButtonSFS
                              clientName=""
                              entityId={data.tpid}
                              entityReference=""
                              entityName="THIRDPARTY"
                              currcode=""
                              funcode="evUpdateAuthamount"
                              dataInput={data}
                              wizardsteps
                              refetch={() => refetch()}
                              width={560}
                              title="updateauthamount"
                              titlewithName={data?.prcodelabel}
                            >
                              <IPen
                                className="font-size-18"
                                style={{ cursor: 'pointer' }}
                              />
                            </TaskSButtonSFS>
                          </Col>
                        )}
                        {data.authToleranceStatus === 'ACTIVE' &&
                          data.authToleranceList?.length > 0 && (
                            <Col>
                              <span className="d-flex align-items-center">
                                <IUplift className="mr-1" />
                                <span
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                  }}
                                >
                                  {data.authToleranceList[0].toleranceType ===
                                    'PERCENT'
                                    ? `+${data.authToleranceList[0].toleranceRate} %`
                                    : toCurrency(
                                      data.authToleranceList[0]
                                        .toleranceAmount
                                    )}
                                </span>
                              </span>
                            </Col>
                          )}
                      </Row>
                    </div>
                  </Col>
                  <Col xs={24} sm={8} md={8}>
                    <div style={{ textAlign: 'right' }}>
                      <p
                        className="gray-color font-weight-500 font-size-13"
                        style={{ marginBottom: '2px', lineHeight: '16px' }}
                      >
                        <GetLabel label="mpea" />
                      </p>
                      <Row
                        justify="end"
                        align="middle"
                        wrap={false}
                        gutter={8}
                        style={{ marginRight: '-3rem' }}
                      >
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                          <span
                            className="font-size-18 font-weight-500"
                            style={{ lineHeight: '24px' }}
                          >
                            {toCurrency(data?.authcalculatedamount)}
                          </span>
                        </Col>
                        {tptype === 'CONC' && (
                          <Col flex="none">
                            <TaskSButtonSFS
                              clientName=""
                              entityId={data.tpid}
                              entityReference=""
                              entityName="THIRDPARTY"
                              currcode=""
                              funcode="evUpdateMPEAamount"
                              wizardsteps
                              dataInput={data}
                              refetch={() => {
                                refetch();
                              }}
                              width={560}
                              title="evupdatempeaamount"
                              titlewithName={`${'"'}${data?.prcodelabel}${'"'}`}
                            >
                              <IPen
                                className="font-size-18"
                                style={{ cursor: 'pointer' }}
                              />
                            </TaskSButtonSFS>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                </Row>

                {/* Third Row - Remaining Available */}
                <Row
                  gutter={[16, 8]}
                  style={{ marginBottom: '16px' }}
                  align="top"
                >
                  <Col xs={24} sm={16} md={16}>
                    <div>
                      <p
                        className="gray-color font-weight-500 font-size-13"
                        style={{ marginBottom: '2px', lineHeight: '16px' }}
                      >
                        <GetLabel label="remainingavailablenl" />
                      </p>
                      <p
                        className="font-weight-500 font-size-20"
                        style={{ margin: 0, lineHeight: '28px' }}
                      >
                        {toCurrency(data?.authavailableamount)}
                      </p>
                    </div>
                  </Col>
                  <Col xs={24} sm={8} md={8}>
                    <div style={{ textAlign: 'right' }}>
                      <p
                        className="font-weight-500 gray-color font-size-13"
                        style={{ marginBottom: '2px', lineHeight: '16px' }}
                      >
                        <GetLabel label="authavailableamountmpea" />
                      </p>
                      <p
                        className="font-weight-500 font-size-20"
                        style={{ margin: 0, lineHeight: '28px' }}
                      >
                        {toCurrency(data?.authavailableamountmpea)}
                      </p>
                    </div>
                  </Col>
                </Row>

                {/* Fourth Row - Permeability (CONC only) */}
                {tptype === 'CONC' && (
                  <Row gutter={[16, 8]} align="bottom">
                    <Col xs={24} sm={16} md={16}>
                      <div>
                        <p
                          className="gray-color font-weight-500 font-size-13"
                          style={{ marginBottom: '2px', lineHeight: '16px' }}
                        >
                          <GetLabel label="availableamountwithpermeab" />
                        </p>
                        <p
                          className="font-weight-500 font-size-20"
                          style={{ margin: 0, lineHeight: '28px' }}
                        >
                          {toCurrency(
                            data?.authavailableamountwithpermeability
                          )}
                        </p>
                      </div>
                    </Col>
                    <Col xs={24} sm={8} md={8}>
                      <div style={{ textAlign: 'right' }}>
                        <p
                          className="font-weight-500 gray-color font-size-13"
                          style={{ marginBottom: '2px', lineHeight: '16px' }}
                        >
                          <GetLabel label="availableamountmpeawithpermeab" />
                        </p>
                        <p
                          className="font-weight-500 font-size-20"
                          style={{ margin: 0, lineHeight: '28px' }}
                        >
                          {toCurrency(
                            data?.authavailableamountmpeawithpermeability
                          )}
                        </p>
                      </div>
                    </Col>
                  </Row>
                )}
              </Col>

              {/* Right Column - Progress Charts */}
              <Col xs={24} sm={24} md={10} lg={10} xl={10}>
                <Row
                  gutter={[16, 16]}
                  justify="center"
                  align="bottom"
                  style={{ height: '100%', minHeight: '200px' }}
                >
                  <Col xs={12} sm={12} md={24} lg={12} xl={12}>
                    <div style={{ textAlign: 'center' }}>
                      <ProgressBarChart
                        percentageValue={Number(
                          data?.pourcentageConsumedNLimit.toFixed(2)
                        )}
                        size={120}
                        label={
                          <>
                            <GetLabel label="consumedof" />{' '}
                            {toEuroSimpleDisplayK(data?.authamount)}
                          </>
                        }
                        labelAbove="Normal limit"
                        multicolor
                        color="#3B55B9"
                      />
                    </div>
                  </Col>
                  <Col xs={12} sm={12} md={24} lg={12} xl={12}>
                    <div style={{ textAlign: 'center' }}>
                      <ProgressBarChart
                        percentageValue={Number(
                          typeof data?.pourcentageConsumedMPEA === 'number'
                            ? data.pourcentageConsumedMPEA.toFixed(2)
                            : 0
                        )}
                        size={120}
                        label={
                          <>
                            <GetLabel label="consumedof" />{' '}
                            {toEuroSimpleDisplayK(data?.authcalculatedamount)}
                          </>
                        }
                        labelAbove="MPEA"
                        multicolor
                        color="#3B55B9"
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          {/* Right Sidebar */}
          <Col
            xs={24}
            sm={24}
            md={6}
            lg={6}
            xl={6}
            style={{
              padding: '2rem',
              background: '#F5F6FA80',
              borderLeft: '3px dotted #6B7181',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '12px',
              minHeight: '400px',
            }}
          >
            {/* Status Rows */}
            {tptype === 'CONC' && (
              <Row
                gutter={8}
                align="middle"
                wrap={false}
                style={{ marginBottom: '8px', zIndex: 4 }}
              >
                <Col flex="auto">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '4px',
                    }}
                  >
                    {data?.authstatus === 'INIT' ? (
                      <GetLabel
                        style={{ fontSize: '15px' }}
                        label="waitingActivation"
                      />
                    ) : data?.authstatus === 'ACTIVE' && isExpired ? (
                      <>
                        <CloseOutlined
                          style={{ fontSize: 18, marginRight: 4 }}
                          className="color-red"
                        />
                        <GetLabel
                          className="font-size-15 font-weight-500"
                          label="authinactive"
                        />
                      </>
                    ) : data?.authstatus === 'ACTIVE' ? (
                      <>
                        <CheckOutlined
                          style={{
                            fontSize: 18,
                            marginRight: 4,
                            color: '#0A7C53',
                          }}
                        />
                        <GetLabel
                          className="font-size-15 font-weight-500"
                          label="limtActive"
                        />
                        <ArrowRightOutlined style={{ margin: '0 4px' }} />
                        <span className="font-size-15 font-weight-500">
                          {data.enddate}
                        </span>
                      </>
                    ) : (
                      <>
                        <CloseOutlined
                          style={{ fontSize: 18, marginRight: 4 }}
                          className="color-red"
                        />
                        <GetLabel
                          className="font-size-15 font-weight-500"
                          label="authinactive"
                        />
                      </>
                    )}
                  </div>
                </Col>
                <Col flex="none">
                  <TaskSButtonSFS
                    clientName=""
                    entityId={data.tpid}
                    entityReference=""
                    entityName="THIRDPARTY"
                    currcode=""
                    funcode="evUpdateAuthStatus"
                    wizardsteps
                    dataInput={data}
                    refetch={() => {
                      refetch();
                    }}
                    width={560}
                    title="evupdateauthstatus"
                    titlewithName={data?.prcodelabel}
                  >
                    <IPen
                      className="font-size-18"
                      style={{ cursor: 'pointer' }}
                    />
                  </TaskSButtonSFS>
                </Col>
              </Row>
            )}

            <Row
              gutter={8}
              align="middle"
              wrap={false}
              style={{ marginBottom: '8px' }}
            >
              <Col flex="auto">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {data?.flagexcludpermeabilitypm == false ||
                    data?.flagexcludpermeabilitypm == null ? (
                    <>
                      <CheckOutlined
                        style={{
                          fontSize: 18,
                          marginRight: 4,
                          color: '#0A7C53',
                        }}
                      />
                      <GetLabel
                        className="font-size-15 font-weight-500"
                        label="permeabilityactive"
                      />
                    </>
                  ) : (
                    <>
                      <CloseOutlined
                        style={{ fontSize: 18, marginRight: 4 }}
                        className="color-red"
                      />
                      <GetLabel
                        className="font-size-15 font-weight-500"
                        label="permeabilityinactive"
                      />
                    </>
                  )}
                </div>
              </Col>
              <Col flex="none">
                <TaskSButtonSFS
                  clientName=""
                  entityId={data.tpid}
                  entityReference=""
                  entityName="THIRDPARTY"
                  currcode=""
                  funcode="evActivatePermeability"
                  wizardsteps
                  dataInput={data}
                  refetch={() => {
                    refetch();
                  }}
                  width={560}
                  title="evactivatepermeability"
                  titlewithName={data?.prcodelabel}
                >
                  <IPen
                    className="font-size-18"
                    style={{ cursor: 'pointer' }}
                  />
                </TaskSButtonSFS>
              </Col>
            </Row>

            {tptype === 'CONC' && (
              <Row
                gutter={8}
                align="middle"
                wrap={false}
                style={{ marginBottom: '8px' }}
              >
                <Col flex="auto">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '4px',
                    }}
                  >
                    {data?.authToleranceStatus === 'ACTIVE' ? (
                      <>
                        <CheckOutlined
                          style={{
                            fontSize: 18,
                            marginRight: 4,
                            color: '#0A7C53',
                          }}
                        />
                        <GetLabel
                          className="font-size-15 font-weight-500"
                          label="overactivityActive"
                        />
                        {data?.authToleranceList?.[0]?.status === 'ACTIVE' && (
                          <>
                            <ArrowRightOutlined style={{ margin: '0 4px' }} />
                            <span className="font-size-15 font-weight-500">
                              {data.authToleranceList[0].startDate} →{' '}
                              {data.authToleranceList[0].endDate}
                            </span>
                          </>
                        )}
                      </>
                    ) : data?.authToleranceStatus === 'SCHEDULED' ? (
                      <>
                        <ClockCircleOutlined
                          style={{ fontSize: 18, marginRight: 4 }}
                          className="color-orange"
                        />
                        <GetLabel
                          className="font-size-15 font-weight-500"
                          label="overactivityscheduled"
                        />
                      </>
                    ) : (
                      <>
                        <CloseOutlined
                          style={{ fontSize: 18, marginRight: 4 }}
                          className="color-red"
                        />
                        <GetLabel
                          className="font-size-15 font-weight-500"
                          label="overactivityinactive"
                        />
                      </>
                    )}
                  </div>
                </Col>
                <Col flex="none">
                  <TaskSButtonSFS
                    clientName=""
                    entityId={data.tpid}
                    entityReference=""
                    entityName="THIRDPARTY"
                    currcode=""
                    funcode="evUpdateTPOveractivity"
                    wizardsteps
                    dataInput={data}
                    refetch={() => {
                      refetch();
                    }}
                    width={560}
                    title="evupdatetpoveractivity"
                    titlewithName={data?.prcodelabel}
                  >
                    <IPen
                      className="font-size-18"
                      style={{ cursor: 'pointer' }}
                    />
                  </TaskSButtonSFS>
                </Col>
              </Row>
            )}

            {tptype === 'CONC' && (
              <Row gutter={8} align="middle" wrap={false}>
                <Col flex="auto">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '4px',
                    }}
                  >
                    <WarningOutlined
                      style={{ fontSize: 18, marginRight: 4 }}
                      className="color-orange"
                    />
                    <GetLabel
                      className="font-size-15 font-weight-500"
                      label="creditcontrol"
                    />
                    <span className="font-size-15 font-weight-500">
                      {data?.creditcontrollabel ?? data?.creditcontrol}
                    </span>
                  </div>
                </Col>
                <Col flex="none">
                  <TaskSButtonSFS
                    clientName=""
                    entityId={data.tpid}
                    entityReference=""
                    entityName="THIRDPARTY"
                    currcode=""
                    funcode="evUpdateCreditControl"
                    wizardsteps
                    dataInput={data}
                    refetch={() => {
                      refetch();
                    }}
                    width={560}
                    title="evupdatecreditcontrol"
                    titlewithName={data?.prcodelabel}
                  >
                    <IPen
                      className="font-size-18"
                      style={{ cursor: 'pointer' }}
                    />
                  </TaskSButtonSFS>
                </Col>
              </Row>
            )}
            <Row gutter={8} align="middle" wrap={false}>
              <Col flex="auto">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '4px',
                  }}
                >
                  <InfoCircleOutlined
                    style={{ fontSize: 18, marginRight: 4 }}
                    className="color-blue"
                  />

                  {loadingOfferCode ? (
                    <Spin size="small" />
                  ) : (
                    <span className="font-size-15 font-weight-500">
                      {offerCode}
                    </span>
                  )}
                </div>
              </Col>
              <Col flex="none">
                {/* You can add an edit button here if needed for bonification */}
              </Col>
            </Row>
            {/* Bonification Row - Now in the right sidebar */}
            {tptype === 'CONC' && (
              <>
                <Row gutter={8} align="middle" wrap={false}>
                  <Col flex="auto">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '4px',
                      }}
                    >
                      <InfoCircleOutlined
                        style={{ fontSize: 18, marginRight: 4 }}
                        className="color-orange"
                      />
                      <GetLabel
                        className="font-size-15 font-weight-500"
                        label="bonification"
                      />
                      {/* Bonification badge - inline */}
                      {loadingBonification ? (
                        <Spin size="small" style={{ marginLeft: '8px' }} />
                      ) : (
                        <span
                          style={{
                            marginLeft: '8px',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            backgroundColor:
                              bonificationData?.taux &&
                                bonificationData.taux > 0
                                ? '#fff7e6'
                                : '#fafafa',
                            border: `1px solid ${bonificationData?.taux &&
                                bonificationData.taux > 0
                                ? '#ffd666'
                                : '#d9d9d9'
                              }`,
                            fontSize: '11px',
                            fontWeight: 700,
                            color:
                              bonificationData?.taux &&
                                bonificationData.taux > 0
                                ? '#d48806'
                                : '#666666',
                            display: 'inline-block',
                          }}
                        >
                          {bonificationData?.taux !== undefined
                            ? bonificationData.taux > 0
                              ? `+${bonificationData.taux}%`
                              : `${bonificationData.taux}%`
                            : '0%'}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col flex="none">
                    {/* You can add an edit button here if needed for bonification */}
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ActivityCard;
